// LoginScreen — username + password (Pass 2.4).
//
// Phase 6.3.2 (A.3) UX fixes:
//   * Client validation — empty fields show inline "Required"
//     beneath each, and submit makes NO network call.
//   * The credentials error moved to the TOP of the form (above
//     the username field) so the layout doesn't shift when the
//     first failure appears.
//   * "Show password" toggle (consistency with AddUserModal).
// Typography uses the 6.3.1 scale (.text-display / .text-caption).

import { useState, type FormEvent } from "react";
import { AlertCircle, ArrowRight, Eye, EyeOff, Loader2 } from "lucide-react";

import { ApiError, getApiBaseUrl, login as apiLogin } from "../lib/api";
import { useAuth } from "../state/AuthContext";

interface FieldErrors {
  username?: string;
  password?: string;
}

export function LoginScreen() {
  const { login: setAuth } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const serviceUrl = getApiBaseUrl();

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;

    // A.3: client validation — show "Required" inline, no network
    // call when either field is empty.
    const errs: FieldErrors = {};
    if (username.trim().length === 0) errs.username = "Required";
    if (password.length === 0) errs.password = "Required";
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setError(null);
    setBusy(true);

    try {
      const r = await apiLogin(username.trim(), password);
      setAuth({ token: r.token, expires_at: r.expires_at });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError("Invalid username or password");
      } else if (err instanceof ApiError && err.status === 0) {
        setError(`Cannot reach service at ${serviceUrl}`);
      } else if (err instanceof ApiError) {
        setError(`Sign-in failed: ${err.message}`);
      } else {
        setError(
          err instanceof Error ? `Sign-in failed: ${err.message}` : "Sign-in failed",
        );
      }
      setBusy(false);
      return;
    }
    // Success — AuthContext flips state, App routes to /chooser.
  }

  return (
    <div
      data-testid="login-screen"
      className="h-screen flex flex-col items-center justify-center p-loose"
    >
      <div className="w-full max-w-md glass-card rounded-3xl p-8 shadow-[0_24px_60px_rgba(0,0,0,0.45)]">
        <header className="mb-section flex flex-col items-center text-center">
          <div
            aria-hidden
            style={{ background: "linear-gradient(135deg, #8b6cff, #6366f1)" }}
            className="w-14 h-14 rounded-2xl flex items-center justify-center text-white shadow-[0_6px_20px_rgba(124,92,255,0.5)] mb-4"
          >
            <span className="text-[26px] font-bold leading-none">✦</span>
          </div>
          <h1 className="text-h1 font-bold tracking-tightish">Welcome back</h1>
          <p className="mt-1.5 text-body-sm text-theme-text-secondary">
            Sign in to your Slack archive
          </p>
        </header>

        <form onSubmit={onSubmit} className="space-y-loose">
          {/* A.3 + 6.3.2.1 (BUG 2): error at the TOP, in a fixed-
              height reserved container that's ALWAYS in the layout
              (just empty when there's no error) — so the submit
              button's Y position never shifts between the no-error
              and error states. */}
          <div className="min-h-[44px]" role="status" aria-live="polite" data-testid="login-error-slot">
            {error && (
              <div
                role="alert"
                data-testid="login-error"
                className="p-default bg-theme-error-bg border-l-2 border-theme-error-border flex gap-snug items-start"
              >
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-theme-error-text" aria-hidden />
                <span className="text-body-sm text-theme-error-text leading-relaxed">{error}</span>
              </div>
            )}
          </div>

          <div>
            <label htmlFor="username" className="block text-caption mb-1.5">
              Username or email
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(ev) => {
                setUsername(ev.target.value);
                if (fieldErrors.username) setFieldErrors((f) => ({ ...f, username: undefined }));
              }}
              autoComplete="username"
              autoFocus
              disabled={busy}
              data-testid="login-username"
              aria-invalid={fieldErrors.username ? "true" : "false"}
              placeholder="username or email"
              className={[
                "w-full px-3.5 py-2.5 rounded-xl glass-input font-mono text-sm text-theme-text-primary placeholder-theme-text-tertiary focus:outline-none disabled:opacity-50",
                fieldErrors.username ? "border-theme-error-border focus:border-theme-error-border" : "",
              ].join(" ")}
            />
            {fieldErrors.username && (
              <p data-testid="login-username-error" className="mt-1 text-caption text-theme-error-text">
                {fieldErrors.username}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="password" className="block text-caption mb-1.5">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(ev) => {
                  setPassword(ev.target.value);
                  if (fieldErrors.password) setFieldErrors((f) => ({ ...f, password: undefined }));
                }}
                autoComplete="current-password"
                disabled={busy}
                data-testid="login-password"
                aria-invalid={fieldErrors.password ? "true" : "false"}
                className={[
                  "w-full px-3.5 py-2.5 pr-10 rounded-xl glass-input font-mono text-sm text-theme-text-primary placeholder-theme-text-tertiary focus:outline-none disabled:opacity-50",
                  fieldErrors.password ? "border-theme-error-border focus:border-theme-error-border" : "",
                ].join(" ")}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                disabled={busy}
                data-testid="login-password-toggle"
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute inset-y-0 right-0 px-3 flex items-center text-theme-text-tertiary hover:text-theme-text-primary transition-colors duration-150 disabled:opacity-50"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" strokeWidth={1.5} aria-hidden />
                ) : (
                  <Eye className="w-4 h-4" strokeWidth={1.5} aria-hidden />
                )}
              </button>
            </div>
            {fieldErrors.password && (
              <p data-testid="login-password-error" className="mt-1 text-caption text-theme-error-text">
                {fieldErrors.password}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit"
            style={{ background: "linear-gradient(135deg, #8b6cff, #6366f1)" }}
            className="w-full px-4 py-3 rounded-xl text-white text-sm font-semibold flex items-center justify-center gap-snug shadow-[0_8px_22px_rgba(124,92,255,0.4)] transition-opacity duration-150 hover:opacity-95 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2} aria-hidden />
                Signing in…
              </>
            ) : (
              <>
                Sign in
                <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
              </>
            )}
          </button>

          {/* Aurora: trust footer (Figma) — honest copy. */}
          <div className="flex items-center justify-center gap-2 pt-1">
            <span className="w-1.5 h-1.5 rounded-full bg-theme-success-text shadow-[0_0_5px_var(--theme-success-text)]" aria-hidden />
            <span className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-theme-text-muted">
              Self-hosted · Private archive
            </span>
          </div>
        </form>
      </div>
    </div>
  );
}
